import React, { useState, useEffect, useRef } from 'react';
import notificationService from '../../services/notificationService';
import TeamsWebhookField from './TeamsWebhookField';
import './NotificationsPanel.css';

/**
 * Shared, controlled notifications editor — verified recipient emails + a verified
 * Microsoft Teams webhook. Used by the Payment Porter wizard, the Sync project wizard,
 * and the Sync settings tab.
 *
 * Props:
 *   value         { emails: [{ id?, email, source }], teamsWebhook: string|null }
 *   onChange(value)  called whenever a recipient or the webhook is committed
 *   hotelId, accountId  used to fetch hotel/chain default emails (optional)
 *   showDefaults  whether to fetch + offer hotel/chain default checkboxes (default true)
 *   persistence   optional { addEmail({email,source})=>{id}, removeEmail(id), saveWebhook(url|null) }.
 *                 When provided (settings mode) commits persist immediately; otherwise (wizard
 *                 mode) the parent persists `value` on submit.
 *   maxCustom     max additional (custom) emails (default 3)
 *   service       notification service (defaults to the shared one; injectable for tests)
 */
const NotificationsPanel = ({
    value,
    onChange,
    hotelId,
    accountId,
    showDefaults = true,
    persistence = null,
    maxCustom = 3,
    service = notificationService,
}) => {
    const emails       = value?.emails || [];
    const teamsWebhook = value?.teamsWebhook || null;

    const [defaults, setDefaults]               = useState({ hotelEmail: null, chainEmail: null });
    const [defaultsLoading, setDefaultsLoading] = useState(false);
    const defaultsAppliedRef                    = useRef(false);

    // Pending custom-email slots (in-progress add/verify before they join `value.emails`).
    const [slots, setSlots] = useState([]);

    const hasEmail = (email) => emails.some(e => e.email.toLowerCase() === email.trim().toLowerCase());
    const customEmails = emails.filter(e => e.source === 'custom');

    // ── commit helpers (persist immediately when in settings mode) ──────────────
    const commitAdd = async (email, source) => {
        const clean = email.trim().toLowerCase();
        if (hasEmail(clean)) return;
        let id;
        if (persistence) {
            const res = await persistence.addEmail({ email: clean, source });
            id = res?.id;
        }
        onChange({ ...value, emails: [...emails, { id, email: clean, source }] });
    };

    const commitRemove = async (entry) => {
        if (persistence && entry.id) await persistence.removeEmail(entry.id);
        onChange({ ...value, emails: emails.filter(e => e !== entry) });
    };

    const commitWebhook = async (url) => {
        if (persistence) await persistence.saveWebhook(url);
        onChange({ ...value, teamsWebhook: url });
    };

    // ── load hotel/chain default emails ─────────────────────────────────────────
    useEffect(() => {
        if (!showDefaults || (!hotelId && !accountId)) return;
        let cancelled = false;
        setDefaultsLoading(true);
        service.getDefaults(hotelId, accountId)
            .then(data => { if (!cancelled) setDefaults(data || {}); })
            .catch(() => { /* non-fatal */ })
            .finally(() => { if (!cancelled) setDefaultsLoading(false); });
        return () => { cancelled = true; };
    }, [hotelId, accountId, showDefaults, service]);

    // In wizard mode (no persistence) hotel/chain defaults start checked, mirroring the
    // original behaviour. Apply once, after defaults load, only if not already present.
    useEffect(() => {
        if (persistence || defaultsAppliedRef.current) return;
        if (!defaults.hotelEmail && !defaults.chainEmail) return;
        defaultsAppliedRef.current = true;
        const additions = [];
        if (defaults.hotelEmail && !hasEmail(defaults.hotelEmail)) additions.push({ email: defaults.hotelEmail.toLowerCase(), source: 'hotel' });
        if (defaults.chainEmail && defaults.chainEmail !== defaults.hotelEmail && !hasEmail(defaults.chainEmail)) additions.push({ email: defaults.chainEmail.toLowerCase(), source: 'chain' });
        if (additions.length) onChange({ ...value, emails: [...emails, ...additions] });
    }, [defaults]); // eslint-disable-line

    const toggleDefault = (email, source, checked) => {
        if (checked) return commitAdd(email, source);
        const entry = emails.find(e => e.email.toLowerCase() === email.toLowerCase());
        if (entry) return commitRemove(entry);
    };

    // ── custom-slot operations ──────────────────────────────────────────────────
    const updateSlot = (i, patch) => setSlots(prev => prev.map((s, idx) => idx === i ? { ...s, ...patch } : s));
    const addSlot = () => setSlots(prev => [...prev, { email: '', codeSent: false, code: '', verifying: false, sending: false, error: null }]);
    const removeSlot = (i) => setSlots(prev => prev.filter((_, idx) => idx !== i));

    const sendSlotCode = async (i, slot) => {
        updateSlot(i, { sending: true, error: null });
        try {
            await service.sendEmailCode(slot.email.trim());
            updateSlot(i, { codeSent: true, sending: false });
        } catch (err) {
            updateSlot(i, { sending: false, error: err.response?.data?.message || 'Failed to send code' });
        }
    };

    const verifySlotCode = async (i, slot) => {
        updateSlot(i, { verifying: true, error: null });
        try {
            await service.verifyEmailCode(slot.email.trim(), slot.code);
            await commitAdd(slot.email, 'custom');
            removeSlot(i);
        } catch (err) {
            updateSlot(i, { verifying: false, error: err.response?.data?.message || 'Invalid code' });
        }
    };

    const customCount = customEmails.length + slots.length;
    const checkmark = (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ color: '#16a34a', flexShrink: 0 }}>
            <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    return (
        <div className="notif-panel">
            {defaultsLoading && <div className="notif-loading">Loading hotel and chain emails…</div>}

            {showDefaults && (defaults.hotelEmail || defaults.chainEmail) && (
                <div className="notif-trusted-section">
                    <div className="notif-section-label">Hotel &amp; Chain Emails</div>
                    {defaults.hotelEmail && (
                        <label className="notif-check-row">
                            <input type="checkbox" checked={hasEmail(defaults.hotelEmail)} onChange={e => toggleDefault(defaults.hotelEmail, 'hotel', e.target.checked)} />
                            <span className="notif-email-text">{defaults.hotelEmail}</span>
                            <span className="notif-source-badge">Hotel</span>
                        </label>
                    )}
                    {defaults.chainEmail && defaults.chainEmail !== defaults.hotelEmail && (
                        <label className="notif-check-row">
                            <input type="checkbox" checked={hasEmail(defaults.chainEmail)} onChange={e => toggleDefault(defaults.chainEmail, 'chain', e.target.checked)} />
                            <span className="notif-email-text">{defaults.chainEmail}</span>
                            <span className="notif-source-badge">Chain</span>
                        </label>
                    )}
                </div>
            )}

            <div className="notif-section-label" style={{ marginTop: '1.25rem' }}>Additional Emails</div>
            <p className="notif-help-text">Add up to {maxCustom} more recipients. Each address must be verified with a code.</p>

            {customEmails.map((entry, i) => (
                <div key={`v-${entry.id ?? entry.email}-${i}`} className="notif-custom-slot">
                    <div className="notif-verified-row">
                        {checkmark}
                        <span className="notif-email-text">{entry.email}</span>
                        <span style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>Verified</span>
                        <button className="notif-remove-btn" onClick={() => commitRemove(entry)}>✕</button>
                    </div>
                </div>
            ))}

            {slots.map((slot, i) => (
                <div key={`s-${i}`} className="notif-custom-slot">
                    <div className="notif-custom-row">
                        <input
                            className="notif-input notif-email-input"
                            type="email"
                            placeholder={`Email address ${customEmails.length + i + 1}`}
                            value={slot.email}
                            disabled={slot.codeSent}
                            onChange={e => updateSlot(i, { email: e.target.value, error: null })}
                        />
                        {!slot.codeSent ? (
                            <button className="notif-send-btn" disabled={!slot.email.trim() || slot.sending} onClick={() => sendSlotCode(i, slot)}>
                                {slot.sending ? 'Sending…' : 'Send Code'}
                            </button>
                        ) : (
                            <button className="notif-resend-btn" onClick={() => updateSlot(i, { codeSent: false, code: '' })}>Change</button>
                        )}
                        <button className="notif-remove-btn" onClick={() => removeSlot(i)} title="Remove">✕</button>
                    </div>
                    {slot.codeSent && (
                        <div className="notif-custom-row" style={{ marginTop: '0.5rem' }}>
                            <input
                                className="notif-input notif-code-input"
                                type="text"
                                placeholder="6-digit code"
                                maxLength={6}
                                value={slot.code}
                                onChange={e => updateSlot(i, { code: e.target.value.replace(/\D/g, ''), error: null })}
                            />
                            <button className="notif-verify-btn" disabled={slot.code.length !== 6 || slot.verifying} onClick={() => verifySlotCode(i, slot)}>
                                {slot.verifying ? 'Verifying…' : 'Verify'}
                            </button>
                        </div>
                    )}
                    {slot.error && <div className="notif-error-msg" style={{ marginTop: '0.25rem' }}>{slot.error}</div>}
                </div>
            ))}

            {customCount < maxCustom && (
                <button type="button" className="notif-add-btn" onClick={addSlot}>+ Add Email</button>
            )}

            {/* Microsoft Teams webhook — the shared field (TeamsWebhookField), verified before it is kept. */}
            <div className="notif-teams-field">
                <label className="notif-label">Microsoft Teams Webhook URL</label>
                <span className="notif-label-hint"> — optional, receives the same alerts in Teams</span>
                <TeamsWebhookField value={teamsWebhook} onCommit={commitWebhook} service={service} />
            </div>
        </div>
    );
};

export default NotificationsPanel;
